import graphql__8ebd9f1d8195395a6127 from "./__generated__/MapElementsVectorGridTooltipQuery.graphql.js";/* global L */

import { Suspense, useState, useCallback, useEffect, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Marker, useMap } from "react-leaflet";

import { useIsNonDesktop } from "../../Utils/ResponsiveQueries";
import { useMapContext } from "../MapContext";
import { useMoveMapCenterTo } from "../moveMapCenterTo";
import MapTooltip from "../MapTooltip";
import RichText from "../../Utils/RichText";
import Spinner from "../../Utils/Spinner";

const MapElementsVectorGridTooltipContent = props => {
  const { mapElementGeometryId } = props;

  // Load the tooltip content for the given map element geometry.
  const {
    mapElementGeometry: { tooltip }
  } = useLazyLoadQuery(MapElementsVectorGridTooltipQuery, { mapElementGeometryId });

  if (!tooltip) return null;

  // The content is rendered through RichText so that the images a compound feature
  // displays from its source file are constrained to the tooltip width, as the tooltip
  // is way narrower than the pages the other rich contents are displayed in.
  return <RichText content={tooltip.content} />;
};

// This component renders a hidden marker just next to the current cursor position and
// displays the tooltip content, allowing us to render custom tooltips for a given vector
// tile hovered layer.
//
// This trick is needed as the Leaflet.VectorGrid library does not support layer specific
// tooltip contents, and there's no other way of rendering a native leaflet tooltip on top
// of the vector grid layer.
//
const MapElementsVectorGridTooltip = props => {
  const { hoveredMapElementGeometryId, onTappedTooltipDismissed, tappedTooltip } = props;

  const isNonDesktop = useIsNonDesktop();
  const leafletMap = useMap();
  const { registerMapClickedHandler, unregisterMapClickedHandler } = useMapContext();
  const moveMapCenterTo = useMoveMapCenterTo();
  const [hoveredPosition, setHoveredPosition] = useState(null);

  const handleMouseMoved = useCallback(e => setHoveredPosition(e.latlng), []);

  const mapElementGeometryId = isNonDesktop
    ? tappedTooltip?.mapElementGeometryId
    : hoveredMapElementGeometryId;
  const tappedPosition = isNonDesktop ? tappedTooltip?.position : null;

  useEffect(() => {
    if (!tappedTooltip) return;

    // Clear tap state when leaving the mobile layout so it cannot reappear after another
    // breakpoint change.
    if (!isNonDesktop) {
      onTappedTooltipDismissed();

      return;
    }

    const { lat, lng } = tappedTooltip.position;

    // Keep the tapped element in the part of the map that remains visible above panels.
    moveMapCenterTo(lat, lng);
    registerMapClickedHandler(onTappedTooltipDismissed);

    return () => unregisterMapClickedHandler(onTappedTooltipDismissed);
  }, [
    isNonDesktop,
    moveMapCenterTo,
    onTappedTooltipDismissed,
    registerMapClickedHandler,
    tappedTooltip,
    unregisterMapClickedHandler
  ]);

  // A tapped tooltip already has its anchor position. Avoid tracking mouse movements
  // because the compatibility event emitted by the tap has already fired.
  useEffect(() => {
    if (!mapElementGeometryId || tappedPosition) return;

    leafletMap.on("mousemove", handleMouseMoved);

    return () => leafletMap.off("mousemove", handleMouseMoved);
  }, [handleMouseMoved, leafletMap, mapElementGeometryId, tappedPosition]);

  const tooltipPosition = tappedPosition || hoveredPosition;

  // Move the tooltip slightly to the right and down to avoid blinking tooltip issues
  // because the hidden marker is rendered just below the cursor.
  const targetPosition = useMemo(() => {
    if (!tooltipPosition) return null;

    const point = leafletMap.latLngToContainerPoint(tooltipPosition);
    return leafletMap.containerPointToLatLng({ x: point.x + 1, y: point.y + 1 });
  }, [leafletMap, tooltipPosition]);

  // The tapped tooltip is anchored on the element itself rather than on a moving cursor,
  // so it is centered above it : leaflet's default `auto` direction lays the tooltip out
  // entirely on one side of its anchor, which does not fit on a phone width.
  const tooltipDirection = tappedPosition ? "top" : "auto";

  // Create a hidden marker icon that will support the tooltip to be displayed on the map.
  const hiddenTooltipMarkerIcon = useMemo(() => L.divIcon({ iconSize: [0, 0] }), []);

  if (!mapElementGeometryId || !tooltipPosition) return null;

  return (
    <Marker position={targetPosition} icon={hiddenTooltipMarkerIcon}>
      <MapTooltip
        sticky
        permanent
        showOnMobile={!!tappedPosition}
        direction={tooltipDirection}
      >
        <Suspense fallback={<Spinner />}>
          <MapElementsVectorGridTooltipContent
            key={mapElementGeometryId}
            mapElementGeometryId={mapElementGeometryId}
          />
        </Suspense>
      </MapTooltip>
    </Marker>
  );
};

const MapElementsVectorGridTooltipQuery = graphql__8ebd9f1d8195395a6127;

export default MapElementsVectorGridTooltip;
